import { useSeoMeta } from '@unhead/react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, PenSquare } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Layout } from '@/components/Layout';
import { Quiz } from '@/components/Quiz';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { getCategoryByTag, AUTHOR_PUBKEY } from '@/lib/categories';
import { useArticle } from '@/hooks/useArticles';
import { useCurrentUser } from '@/hooks/useCurrentUser';

function formatDate(timestamp: number): string {
  return new Date(timestamp * 1000).toLocaleDateString('de-DE', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function estimateReadingTime(content: string): number {
  const wordsPerMinute = 200;
  const words = content.trim().split(/\s+/).length;
  return Math.max(1, Math.ceil(words / wordsPerMinute));
}

function ArticleSkeleton() {
  return (
    <div className="space-y-8 animate-fade-in">
      <div className="space-y-4">
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-12 w-full max-w-2xl" />
        <Skeleton className="h-6 w-48" />
      </div>
      <Skeleton className="aspect-video w-full rounded-xl" />
      <div className="space-y-4">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-5/6" />
      </div>
    </div>
  );
}

export function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const { data: article, isLoading, error } = useArticle(slug || '');
  const { user } = useCurrentUser();

  const isAuthor = user?.pubkey === AUTHOR_PUBKEY;
  const category = article ? getCategoryByTag(article.category) : undefined;
  const Icon = category?.icon;
  const readingTime = article ? estimateReadingTime(article.content) : 0;

  useSeoMeta({
    title: article ? `${article.title} - wtfisBitcoin` : 'Artikel laden...',
    description: article?.summary || 'wtfisBitcoin Artikel',
  });

  if (!slug) {
    return <Navigate to="/" replace />;
  }

  if (error) {
    return (
      <Layout>
        <div className="container py-16">
          <Card className="border-dashed">
            <CardContent className="py-16 text-center">
              <h2 className="text-xl font-semibold mb-2">Artikel nicht gefunden</h2>
              <p className="text-muted-foreground mb-4">
                Der angeforderte Artikel konnte nicht geladen werden.
              </p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-primary hover:underline"
              >
                <ArrowLeft className="w-4 h-4" />
                Zurück zur Startseite
              </Link>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <article className="container py-8 lg:py-12 max-w-4xl">
        {isLoading ? (
          <ArticleSkeleton />
        ) : article ? (
          <div className="animate-fade-in">
            {/* Back Link */}
            <Link
              to={category ? `/kategorie/${category.id}` : '/'}
              className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-6"
            >
              <ArrowLeft className="w-4 h-4" />
              {category ? `Zurück zu ${category.name}` : 'Zurück'}
            </Link>

            {/* Header */}
            <header className="mb-8">
              {category && (
                <div className="flex items-center gap-2 text-primary font-medium mb-3">
                  {Icon && <Icon className="w-4 h-4" />}
                  <span>{category.name}</span>
                </div>
              )}
              <h1 className="text-3xl lg:text-4xl font-bold mb-4">{article.title}</h1>
              <div className="flex flex-wrap items-center gap-4 text-muted-foreground">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  <span>{formatDate(article.publishedAt)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  <span>{readingTime} Min. Lesezeit</span>
                </div>
                {isAuthor && (
                  <Link to={`/editor/${article.slug}`}>
                    <Button variant="outline" size="sm" className="gap-2">
                      <PenSquare className="w-4 h-4" />
                      Bearbeiten
                    </Button>
                  </Link>
                )}
              </div>
            </header>

            {/* Featured Image */}
            {article.image && (
              <div className="mb-8 rounded-xl overflow-hidden">
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full aspect-video object-cover"
                />
              </div>
            )}

            {/* Summary */}
            {article.summary && (
              <div className="mb-8 p-6 bg-secondary/50 rounded-xl border border-border/50">
                <p className="text-lg text-muted-foreground italic">{article.summary}</p>
              </div>
            )}

            {/* Content */}
            <div className="prose prose-lg dark:prose-invert max-w-none mb-12">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{article.content}</ReactMarkdown>
            </div>

            {/* Quiz Section */}
            {article.quiz && article.quiz.length > 0 && (
              <section className="mt-12 pt-8 border-t border-border">
                <h2 className="text-2xl font-bold mb-6">Teste dein Wissen</h2>
                <Quiz questions={article.quiz} />
              </section>
            )}

            {/* Navigation */}
            <div className="mt-12 pt-8 border-t border-border">
              <Link
                to={category ? `/kategorie/${category.id}` : '/'}
                className="inline-flex items-center gap-2 text-primary hover:underline font-medium"
              >
                <ArrowLeft className="w-4 h-4" />
                Weitere Artikel in {category?.name || 'dieser Kategorie'}
              </Link>
            </div>
          </div>
        ) : (
          <Card className="border-dashed">
            <CardContent className="py-16 text-center">
              <h2 className="text-xl font-semibold mb-2">Artikel nicht gefunden</h2>
              <p className="text-muted-foreground mb-4">
                Der angeforderte Artikel existiert nicht.
              </p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-primary hover:underline"
              >
                <ArrowLeft className="w-4 h-4" />
                Zurück zur Startseite
              </Link>
            </CardContent>
          </Card>
        )}
      </article>
    </Layout>
  );
}
